import {
  GraphQLClient,
  RequestMiddleware,
  ResponseMiddleware,
} from "graphql-request";
import { getDeviceId } from "~/api-client";
import { DeliveryAddress } from "~/types";
import {
  CoordsInput,
  FulfillmentType,
  getSdk,
  GetWebLinksQuery,
  OrderAdjustment,
  OrderAheadItem,
  ProfileScope,
} from "./generated";

interface ApiConfig {
  name?: string;
  apiUrl: string;
  apiKey: string;
  appleWebClientId?: string;
}

export const FEATURE_KEYS: FeatureKey[] = ["web.email-verification"];

export type FeatureKey = "web.email-verification";

export function create(config: ApiConfig, getToken: () => string) {
  const requestMiddleware: RequestMiddleware = async (req) => {
    const token = getToken();
    return {
      ...req,
      headers: {
        ...req.headers,
        authorization: token ? `Bearer ${token}` : "",
        "Tidy-App-Key": config.apiKey,
      },
      url: req.operationName
        ? `${req.url}/${encodeURIComponent(req.operationName)}`
        : req.url,
    };
  };

  const responseMiddleware: ResponseMiddleware = (response) => {
    if (!(response instanceof Error) || !("response" in response)) return;

    // When there is a 401 response will be an error with .name "Error"
    // However it has a .response property available with has the status code
    const errResponse = (response as { response: { status: number } }).response;
    if (errResponse.status === 401) window.location.href = "/logout";
  };

  const graphql = getSdk(
    new GraphQLClient(`${config.apiUrl}/graphql`, {
      requestMiddleware,
      responseMiddleware,
    }),
  );

  // Queries
  async function getActivity(after?: string) {
    return graphql.GetActivity({ after });
  }
  async function getActivitySummary() {
    return graphql.GetActivitySummary();
  }
  async function getAddresses(query: string, locationRef?: string) {
    return graphql.GetAddresses({ query, locationRef });
  }

  async function getAddressDetails(ref: string) {
    return graphql.GetAddressDetails({ ref });
  }

  async function getFeatures() {
    let deviceId = "";
    if (typeof window !== "undefined") {
      deviceId = getDeviceId();
    }

    return graphql.GetFeatures({ keys: FEATURE_KEYS, deviceId });
  }

  async function getCreditDiscount(variables: {
    location: string;
    orderTotal: number;
    orderItems?: OrderAheadItem[];
    fulfillmentType?: FulfillmentType;
    selectedTimeSlot?: string;
    currency?: string;
    orderAdjustments: OrderAdjustment[];
    deliveryAddress?: DeliveryAddress;
  }) {
    return graphql.GetCreditDiscount(variables);
  }

  async function getLocations(lat: number | null, lng: number | null) {
    return graphql.GetLocations({
      nativeOrderingEnabled: true,
      currentCoords: lat && lng ? { lat, lng } : undefined,
    });
  }

  async function getLocation(locationRef: string) {
    return graphql.GetLocation({ locationRef });
  }

  async function getLanguageStrings() {
    return graphql.GetLanguageStrings();
  }

  async function getMerchant() {
    return graphql.GetMerchant();
  }

  async function getStripe() {
    return graphql.GetStripe();
  }

  async function getLoginConfig() {
    return graphql.GetLoginConfig();
  }

  async function getProfileFields() {
    return graphql.GetUserProfileFields({ scope: ProfileScope.Create });
  }

  async function getCurrentUser() {
    return graphql.GetCurrentUser();
  }

  async function getPaymentMethods() {
    return graphql.GetPaymentMethods();
  }

  async function getOrdering(location: string, to?: CoordsInput) {
    return graphql.GetOrdering({
      location,
      to,
    });
  }

  async function getMenus(
    location: string,
    dispatchOption: FulfillmentType | null,
    productIds?: string[],
  ) {
    return graphql.GetMenus({
      location,
      dispatchOption,
      productIds,
    });
  }

  async function getBaseMenus(
    location: string,
    dispatchOption: FulfillmentType | null,
  ) {
    return graphql.GetBaseMenus({
      location,
      dispatchOption,
    });
  }

  async function getProduct(location: string, productId: string) {
    return graphql.GetProduct({
      location,
      productId,
    });
  }

  async function getPromotions(locationRef: string) {
    return graphql.GetPromotions({ locationRef });
  }

  async function getPromotionsHeader(locationRef: string) {
    return graphql.GetPromotionsHeader({ locationRef });
  }

  async function getRewards(endCursor: string) {
    return graphql.GetRewards({ after: endCursor });
  }

  async function getReward(id: string) {
    return graphql.GetReward({ id });
  }

  async function getWebLinks(locationRef: string | null) {
    if (!locationRef) {
      const result: GetWebLinksQuery = { webLinks: [] };
      return result;
    }

    return graphql.GetWebLinks({ locationRef });
  }

  async function checkDeliveryAddress(
    locationRef: string,
    address: DeliveryAddress,
  ) {
    return graphql.CheckDeliveryAddress({ location: locationRef, address });
  }

  async function getRecentCarts(locationRef: string, limit: number) {
    return graphql.GetRecentCarts({ location: locationRef, limit });
  }

  // Mutations

  async function createOrder(variables: {
    id: string;
    locationRef: string;
    total: number;
    items: Array<OrderAheadItem> | OrderAheadItem;
    notes?: string;
    deliveryAddress?: DeliveryAddress;
    serviceRef?: string;
    tip?: number;
    timeSlot?: string;
  }) {
    return graphql.CreateOrder(variables);
  }

  async function completeOrder(orderId: string, externalPayment?: boolean) {
    return graphql.CompleteOrder({ orderId, externalPayment });
  }

  async function updateOrderTip(orderId: string, tip: number) {
    return graphql.UpdateOrderTip({ orderId, tip });
  }

  async function cancelOrder(orderId: string) {
    return graphql.CancelOrder({ orderId });
  }

  async function requestPhoneVerification(phoneNumber: string) {
    return graphql.RequestPhoneVerification({ phoneNumber });
  }

  async function verifyPhone(code: string) {
    return graphql.VerifyPhone({ code });
  }

  async function claimPromotion(promotionId: string, locationRef: string) {
    return graphql.ClaimPromotion({ promotionId, locationRef });
  }

  async function startPromotionRedemption(
    promotionId: string,
    locationRef: string,
  ) {
    return graphql.StartPromotionRedemption({
      promotionId,
      locationRef,
    });
  }

  async function completePromotionRedemption(
    orderId: string,
    locationRef: string,
  ) {
    return graphql.CompletePromotionRedemption({
      orderId,
      locationRef,
    });
  }

  async function cancelPromotionRedemption(orderId: string) {
    return graphql.CancelPromotionRedemption({ orderId });
  }

  async function redeemCode(code: string) {
    return graphql.RedeemCode({ code });
  }

  async function removePaymentMethod(paymentMethod: string) {
    return graphql.RemovePaymentMethod({ paymentMethod });
  }

  async function getMerchantRequiresPhone() {
    return graphql.GetMerchantRequiresPhone();
  }

  async function getPayRequirements() {
    return graphql.GetPayRequirements();
  }

  async function getPayment(paymentId: string) {
    return graphql.GetPayment({ paymentId });
  }

  async function getTransaction(paymentId: string) {
    return graphql.GetTransaction({ paymentId });
  }

  return {
    cancelOrder,
    createOrder,
    completeOrder,
    updateOrderTip,
    getActivity,
    getActivitySummary,
    getAddresses,
    getAddressDetails,
    getCreditDiscount,
    getCurrentUser,
    getPaymentMethods,
    getFeatures,
    getLanguageStrings,
    getLocations,
    getLocation,
    getMerchant,
    getStripe,
    getLoginConfig,
    getOrdering,
    getMenus,
    getPayment,
    getProduct,
    getProfileFields,
    getPromotions,
    getPromotionsHeader,
    requestPhoneVerification,
    verifyPhone,
    claimPromotion,
    startPromotionRedemption,
    completePromotionRedemption,
    cancelPromotionRedemption,
    redeemCode,
    getMerchantRequiresPhone,
    getRewards,
    getReward,
    getWebLinks,
    checkDeliveryAddress,
    removePaymentMethod,
    getPayRequirements,
    getRecentCarts,
    getTransaction,
    getBaseMenus,
  };
}

// Can't get GraphQL client to return structured error
// May need to switch to rawRequest :(
// This parses the raw error message to get the response object
export function parseMutationErrorMessage(message: string): string | undefined {
  const position = message.search(": {");
  if (position > -1) {
    const str = message.substring(position + 2).trim();
    const obj = JSON.parse(str) as {
      response?: { errors?: { message: string }[] };
    };
    if (obj.response?.errors?.length) {
      return obj.response?.errors[0].message;
    }
  }
}

// This copied from the generated code
export interface GqlLocation {
  __typename?: "Location";
  id: string;
  uid: string;
  slug?: string | null;
  name: string;
  phoneNumber?: string | null;
  openingHours?: string | null;
  website?: string | null;
  orderAheadEnabled: boolean;
  orderAtTableEnabled: boolean;
  orderDeliveryEnabled: boolean;
  address?: {
    __typename?: "Address";
    street?: string | null;
    locality?: string | null;
    region?: string | null;
    postalCode?: string | null;
    country?: string | null;
  } | null;
  coords?: {
    __typename?: "Coords";
    latitude: number;
    longitude: number;
  } | null;
  social?: {
    __typename?: "LocationSocialLinks";
    facebook?: string | null;
    twitter?: string | null;
    instagram?: string | null;
  } | null;
  timezone: string;
}
